/**
 * Matching a typed query against the settings catalog.
 *
 * Split from `settingsSearch.ts`, which is the catalog itself and the binding
 * that hands a rendered row its title and anchor. Those are data; this is the
 * algorithm read over that data, and keeping them apart lets the matching rules
 * be read on their own rather than scrolled to past the whole catalog. It is
 * the same split `settingsSearchModel.ts` already made out of
 * `settingsRailSearch.tsx`. Note what it does not do: `settingsSearch.ts` is
 * still well over the 300-line cap in `.agents/rules/frontend-architecture.md`,
 * and moving these functions only stopped it growing. Indexing a row should
 * cost the catalog a few lines and cost this file nothing.
 *
 * @module
 */
import {
  SETTINGS_SEARCH_ITEMS,
  SETTINGS_SECTION_LABELS,
  type SettingsSearchItem,
} from "./settingsSearch";

/**
 * Fold away the differences a person should not have to reproduce to get a
 * hit: case, accents, and runs of whitespace. NFKD splits the accent off as a
 * combining mark so the range strip can remove it, which is what lets "Colour"
 * match a typed "colour" regardless of how either was composed.
 */
function normalizeSearchText(value: string): string {
  return (
    value
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      // `toLowerCase`, not `toLocaleLowerCase`: the latter follows the browser
      // locale, and under Turkish "Interface font" folds to "ınterface font"
      // (dotless i) while a typed "interface" does not -- the entry becomes
      // unfindable for those users only. Settings titles are ASCII identifiers
      // here, so locale-invariant folding is the correct rule.
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim()
  );
}

/** Everything a query is matched against, in one normalized haystack. */
function haystack(item: SettingsSearchItem): string {
  return normalizeSearchText(
    [item.title, SETTINGS_SECTION_LABELS[item.to], ...(item.keywords ?? [])].join(" "),
  );
}

export function searchSettings(
  query: string,
  {
    cmmdSyncEnabled = false,
    isDesktop = false,
    items = SETTINGS_SEARCH_ITEMS,
  }: {
    readonly cmmdSyncEnabled?: boolean;
    readonly isDesktop?: boolean;
    readonly items?: ReadonlyArray<SettingsSearchItem>;
  } = {},
): ReadonlyArray<SettingsSearchItem> {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) return [];

  return items.filter(
    (item) =>
      (cmmdSyncEnabled || item.requiresCmmdSync !== true) &&
      (isDesktop || item.desktopOnly !== true) &&
      haystack(item).includes(normalizedQuery),
  );
}
